<template>
  <div
    :class="['sidebar-custom', props.className]"
    :style="[props.inlineStyles]"
  >
    <div class="sidebar-section">
      <div
        class="is-logo"
        @click="
          () => {
            onClickLogo();
          }
        "
      >
        <img :src="props.logoImage" alt="Logo" />
      </div>
      <div
        class="sidebar-lists"
        :style="[
          props.heightItem && {
            height: `${props.heightItem} ${props.heightItemUnit ? props.heightItemUnit : 'px'}`,
          },
        ]"
      >
        <div v-for="(menu, index) in props.menuList">
          <div
            :key="index"
            :class="[
              'button-box',
              {
                'is-selected': $router.currentRoute.value.fullPath == menu.path,
                'is-active': menu.isShowSubMenu,
              },
            ]"
            @click="
              () => {
                if (
                  !menu?.subMenu &&
                  $router.currentRoute.value.fullPath != menu.path &&
                  menu.path
                ) {
                  onClickSubMenu(index, false);
                  $router.push(menu.path);
                } else {
                  props.onClickSubMenu && props.onClickSubMenu(index);
                }
              }
            "
          >
            <div v-if="!menu?.subMenu" :class="['sidebar-button', {}]">
              <div class="sidebar-image flex-none">
                <img :src="menu.icon" alt="Icon" />
              </div>
              <div class="sidebar-text flex-1">{{ menu.name }}</div>
            </div>
            <div v-else-if="menu?.subMenu">
              <div :class="['sidebar-button']">
                <div class="sidebar-image flex-none">
                  <img :src="menu.icon" alt="Icon" />
                </div>
                <div class="sidebar-text flex-1">{{ menu.name }}</div>
                <div
                  :class="[
                    'sidebar-image-sub flex-none',
                    { 'rotate-img': menu.isShowSubMenu },
                  ]"
                >
                  <img
                    src="/images/icons/ic-arrow-right-black.svg"
                    alt="Icon"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="menu.isShowSubMenu"
            v-for="(sub, index2) in menu.subMenu"
            :key="`${index}-${index2}`"
            :class="[
              'button-box',
              {
                'is-selected': $router.currentRoute.value.fullPath == sub.path,
              },
            ]"
            @click="sub.path && $router.push(sub.path)"
          >
            <div :class="['sidebar-button', {}]">
              <div class="sidebar-image flex-none">
                <img :src="sub.icon" alt="Icon" />
              </div>
              <div class="sidebar-text flex-1">{{ sub.name }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const componentName = "Sidebar";
// Props types: String, Number, Boolean, Array, Object, Function, Promise
const props = defineProps({
  className: {
    type: String,
  },
  inlineStyles: {
    type: Object,
    default: function () {},
  },
  menuList: {
    type: Array,
    default: [],
  },
  logoImage: {
    type: String,
    default: "/images/logos/logo-sidebar.png",
  },
  heightItem: {
    type: Number,
  },
  heightItemUnit: {
    type: String,
  },
  onClickLogo: {
    type: Function,
    default: function () {},
  },
  onClickSubMenu: {
    type: Function,
    default: function () {},
  },
});
</script>

<style lang="scss">
/* Component variables */
.sidebar-custom {
  /* Parent style   ------------------------------ */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: $specific-sidebar-navigation-width;
  padding: 16px 14px 16px 16px;
  background-color: $color-base-white;
  height: 100vh;
  border-right: 1px solid $color-secondary-2-s-200;

  /* Children style ------------------------------ */
  .sidebar-section {
    .is-logo {
      margin-bottom: 20px;

      img {
        width: 32px;
        height: 32px;
        // width: $size-v1sizing-8;
        // height: $size-v1sizing-8;
      }
    }

    .sidebar-lists {
      @include button-transition();
      overflow-y: scroll;

      .button-box {
        height: $semantic-dimension-cta-height-md;
        padding: $py-2 $px-2 $py-2 $px-2;
        border-radius: $semantic-dimension-cta-rounded-md;

        .sidebar-button {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: $space-y-3;

          .sidebar-image img {
            width: $semantic-dimension-icon-md;
            height: $semantic-dimension-icon-md;
            filter: brightness(0) saturate(100%) invert(46%) sepia(18%)
              saturate(284%) hue-rotate(182deg) brightness(93%) contrast(92%);
          }

          .sidebar-image-sub img {
            width: $semantic-dimension-icon-sm;
            height: $semantic-dimension-icon-sm;
            filter: brightness(0) saturate(100%) invert(46%) sepia(18%)
              saturate(284%) hue-rotate(182deg) brightness(93%) contrast(92%);
          }

          .rotate-img {
            transform: rotate(90deg);
          }

          .sidebar-text {
            font-family: $typography-font-family-primary;
            font-size: $semantic-text-body-md-font-size;
            font-weight: 400;
            line-height: $semantic-text-body-md-line-height-leading-none;
            text-align: left;
            color: $specific-sidebar-navigation-foreground-label;
          }
        }

        &.is-active {
          background: $specific-sidebar-navigation-background-active;
          &:hover {
            background: $specific-sidebar-navigation-background-active;
          }
        }

        &.is-selected {
          cursor: default;
          background: $specific-sidebar-navigation-background-selected;
          .sidebar-button {
            .sidebar-text {
              color: $specific-sidebar-navigation-foreground-selected;
            }
            .sidebar-image {
              img {
                filter: brightness(0) invert(1);
              }
            }
          }

          &:hover {
            background: $specific-sidebar-navigation-background-selected;
          }
        }

        &:hover {
          background-color: $specific-sidebar-navigation-background-hover;
        }
      }
    }
  }
}
</style>
